import { useState, useEffect, useCallback } from 'react';
import {
  createCompanyUpdate,
  listCompanyUpdates,
  recordAppointmentUpdate,
} from '../api/updates/updates';

const POLL_MS = 10000;
const UPDATE_EVENT = 'company-appointment-update';

function publishCompanyUpdate(companyId, created) {
  if (typeof window === 'undefined' || !created) return;
  window.dispatchEvent(new CustomEvent(UPDATE_EVENT, {
    detail: { companyId, created },
  }));
}

function prependUpdate(prev, created) {
  return [created, ...prev.filter((row) => row.id !== created.id)].slice(0, 50);
}

export const useAppointmentUpdates = (companyId) => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(Boolean(companyId));
  const [error, setError] = useState(null);

  const loadUpdates = useCallback(async () => {
    if (!companyId) return;

    try {
      const rows = await listCompanyUpdates({ companyId, limit: 50 });
      setUpdates((prev) => {
        const localReadIds = new Set(prev.filter((update) => update.read).map((update) => update.id));
        return rows.map((row) => (
          row.read || localReadIds.has(row.id) ? { ...row, read: true } : row
        ));
      });
      setError(null);
    } catch (err) {
      console.error('Error loading updates:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId) {
      setUpdates([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    loadUpdates();
    const poll = setInterval(loadUpdates, POLL_MS);
    const onExternalUpdate = (event) => {
      const { companyId: eventCompanyId, created } = event.detail || {};
      if (!created || eventCompanyId !== companyId) return;
      setUpdates((prev) => prependUpdate(prev, created));
    };
    window.addEventListener(UPDATE_EVENT, onExternalUpdate);
    return () => {
      clearInterval(poll);
      window.removeEventListener(UPDATE_EVENT, onExternalUpdate);
    };
  }, [companyId, loadUpdates]);

  const addUpdate = useCallback(async (appointment, type, additionalDetails = {}) => {
    if (!companyId || !appointment) return null;

    try {
      const created = await recordAppointmentUpdate({
        companyId,
        appointment,
        type,
        additionalDetails,
      });
      if (created) {
        setUpdates((prev) => prependUpdate(prev, created));
        publishCompanyUpdate(companyId, created);
      }
      return created;
    } catch (err) {
      console.error('Error adding update:', err);
      throw err;
    }
  }, [companyId]);

  const addRawUpdate = useCallback(async (payload) => {
    if (!companyId) return null;

    const created = await createCompanyUpdate({
      companyId,
      update: {
        timestamp: new Date().toISOString(),
        ...payload,
      },
    });
    if (created) {
      setUpdates((prev) => prependUpdate(prev, created));
      publishCompanyUpdate(companyId, created);
    }
    return created;
  }, [companyId]);

  const markUpdatesReadLocal = useCallback((ids) => {
    const idSet = new Set(Array.isArray(ids) ? ids : [ids]);
    setUpdates((prev) =>
      prev.map((update) => (idSet.has(update.id) ? { ...update, read: true } : update))
    );
  }, []);

  return {
    updates,
    loading,
    error,
    addUpdate,
    addRawUpdate,
    markUpdatesReadLocal,
    reload: loadUpdates,
  };
};
